/**
 * Pure helpers for the /pacientes "vigilancia" filter toggles.
 *
 * The toggles are NOT grid columns — they contribute boolean conditions to the
 * server-side AG-Grid `filterModel` that is POSTed to /sinhantb/pacientes/view/.
 * The backend understands a `boolean` filter (schemas.api_req_res.aggrid
 * .BooleanFilterModel) and renders it as `column IS <value>`, so `tiene_contactos`
 * IS true naturally also excludes the NULL (QMO/LPA) rows.
 *
 * Keeping the field mapping and merge logic here (instead of inline in the
 * component) lets it be unit-tested without standing up AG-Grid.
 */

/** Boolean filter entry understood by the backend AgGrid query builder. */
export interface BooleanFilterModel {
  filterType: 'boolean';
  filter: boolean;
}

/** State of the /pacientes vigilancia toggles. */
export interface PacientesToggleState {
  /** "Investigación Epidemiológica de Campo" → tiene_contactos IS true. */
  investigacionEpidemiologica: boolean;
  /** "Bacteriológicamente Confirmado" → bacteriologicamente_confirmado IS true. */
  bacteriologicamenteConfirmado: boolean;
  /** "Pacientes con Georreferenciación" → georreferenciacion IS true. */
  georreferenciacion: boolean;
  /** "Pacientes Egresados" → egresado IS true. */
  pacientesEgresados: boolean;
}

/** View columns each toggle filters on (single source of truth for the helper + tests). */
export const TOGGLE_FILTER_FIELD = {
  investigacionEpidemiologica: 'tiene_contactos',
  bacteriologicamenteConfirmado: 'bacteriologicamente_confirmado',
  georreferenciacion: 'georreferenciacion',
  pacientesEgresados: 'egresado',
} as const;

/**
 * Build the boolean filterModel fragment contributed by the toggles. Only active
 * toggles add an entry, so an inactive toggle never constrains the query.
 */
export function buildToggleFilterModel(
  state: PacientesToggleState,
): Record<string, BooleanFilterModel> {
  const model: Record<string, BooleanFilterModel> = {};
  if (state.investigacionEpidemiologica) {
    model[TOGGLE_FILTER_FIELD.investigacionEpidemiologica] = { filterType: 'boolean', filter: true };
  }
  if (state.bacteriologicamenteConfirmado) {
    model[TOGGLE_FILTER_FIELD.bacteriologicamenteConfirmado] = { filterType: 'boolean', filter: true };
  }
  if (state.georreferenciacion) {
    model[TOGGLE_FILTER_FIELD.georreferenciacion] = { filterType: 'boolean', filter: true };
  }
  if (state.pacientesEgresados) {
    model[TOGGLE_FILTER_FIELD.pacientesEgresados] = { filterType: 'boolean', filter: true };
  }
  return model;
}

/**
 * Merge the toggle filters onto the grid's own filterModel. Grid column filters
 * and toggle filters combine with AND on the backend, so both stay usable
 * together. Toggle fields are not grid columns, so there is never a key clash.
 */
export function mergeToggleFilters(
  gridFilterModel: Record<string, unknown> | undefined,
  state: PacientesToggleState,
): Record<string, unknown> {
  return { ...(gridFilterModel ?? {}), ...buildToggleFilterModel(state) };
}
